import {
	ClusteringAlgorithm,
  Extent,
  FeatureGroup,
  DataQuery
} from "cartovista"

export default class NonHierarchicalDistanceBasedClusteringAlgorithm2 extends ClusteringAlgorithm {

  /**
   * Cluster algorithm that groups features in a clusters by gathering the feature around the created cluster.
   * Cluster are created by looping on each features on the map that are not already in a cluster until all features are included in one cluster.
   *
   * @namespace cartovista.viewer.core.layers.vectors.clustering
   * @extends cartovista.viewer.core.layers.vectors.clustering.ClusteringAlgorithm
   *
   * @class NonHierarchicalDistanceBasedClusteringAlgorithm2
   * @constructor
   *
   * @param {cartovista.viewer.core.Map} map The map associated with this clustering algorithm.
   * @param {Integer} clusterSize The cluster size in pixels.
   */
   constructor(map, clusterSize, interactiveLayer, dataColumn) {
     super();
     this._map = map;
     this._clusterSize = clusterSize;
     this._interactiveLayer = interactiveLayer;
     this._groupByDataColumn = dataColumn;
     this._featureGroupValue;
     this._clusters = [];
     this._currentDistanceBuffer = -1;

     new Promise(function(resolve, reject) {
         if (this._interactiveLayer.featureCount === 0) {
             this._interactiveLayer.addOneTimeEventListener(this._interactiveLayer.EVENTS.FEATURES_ADDED, resolve);
         } else {
             resolve();
         }
     }.bind(this)).then(function() {
         this._getGroupByValues();
     }.bind(this));
   }

    _getGroupByValues() {
        this._interactiveLayer.getSimpleFeatures(null, function(result) {
            for (var i=0; i < result.features.length; i++) {
                new DataQuery([this._groupByDataColumn], [result.features[i].id], null)
                    .execute(function(queryResult) {
                        result.features[i]._clusterGroupByValue = queryResult.values[this._groupByDataColumn.dataTable.id][this._groupByDataColumn.id][0];
                    }, this);
            }
        }, this);
    };

    /**
     * Function that computes FeatureGroup from the list of features given in parameter.
     *
     * @method createClusters
     * @param {cartovista.viewer.core.layers.vectors.Feature[]} features Features to cluster.
     * @param {cartovista.viewer.sources.SpatialCollection} spatialCollection Spatial collection used to cluster the features.
     *
     * @return {cartovista.viewer.core.layers.vectors.FeatureGroup[]} Array of FeatureGroup representing the given feaures.
     */
    createClusters(features, spatialCollection) {
    //REF: https://github.com/googlemaps/android-maps-utils/blob/master/library/src/com/google/maps/android/clustering/algo/NonHierarchicalDistanceBasedAlgorithm.java

        var functionFilterFeatures = function(feature) {
            return !feature.filteredOut && feature.visible && feature.style.visible;
        };

        var functionFilterFeaturesByGroupValue = function(clusterGroupByValue, feature) {
            return feature._clusterGroupByValue === clusterGroupByValue;
        };

        features = features.filter(functionFilterFeatures);

        var distanceBuffer = this._clusterSize * 2 * this._map.extent.width / this._map.width;
        if (Math.abs(distanceBuffer - this._currentDistanceBuffer) / distanceBuffer > 0.01) {

            var clusters = [],
                visitedFeatures = [],
                distanceToCluster = {},
                itemToCluster = {};

            if (spatialCollection && features && features.length > 0) {
                var distanceBuffer2 = distanceBuffer * distanceBuffer;

                var n = features.length;
                for (var i = 0; i < n; i++) {
                    var feature = features[i];

                    if (visitedFeatures.indexOf(feature) >= 0) {
                        // Candidate is already part of another cluster.
                        continue;
                    }

                    var cluster = new FeatureGroup();
                    cluster.pixelRadius = this._clusterSize * 2;
                    cluster.centerFeature = feature;
                    clusters.push(cluster);
                    visitedFeatures.push(feature);
                    distanceToCluster[feature.id] = 0;

                    // Get all the feature inside a small extent around the point
                    // this extent is square, but cluster are circle, so we need to check if feature are inside circle.
                    var searchExtent = new Extent(feature.centroid.x - distanceBuffer, feature.centroid.y - distanceBuffer, feature.centroid.x + distanceBuffer, feature.centroid.y + distanceBuffer);
                    var featuresAround = spatialCollection.spatialIndex.search(searchExtent);
                    featuresAround = featuresAround.filter(functionFilterFeatures);
                    featuresAround = featuresAround.filter(functionFilterFeaturesByGroupValue.bind(this, feature._clusterGroupByValue));

                    var m = featuresAround.length;

                    if (m === 1) {
                        // Only the current marker is in range. Just add the single item to the results.
                        continue;
                    }

                    for (var j = 0; j < m; j++) {
                        var compareFeature = featuresAround[j];
                        if (compareFeature !== feature) {
                            var distance = (feature.centroid.x - compareFeature.centroid.x) * (feature.centroid.x - compareFeature.centroid.x) + (feature.centroid.y - compareFeature.centroid.y) * (feature.centroid.y - compareFeature.centroid.y);
                            if (distance < distanceBuffer2) {
                                var existingDistance = distanceToCluster[compareFeature.id];
                                if (typeof existingDistance !== 'undefined' && existingDistance !== null) {

                                    // Item already belongs to another cluster. Check if it's closer to this cluster.
                                    if (existingDistance < distance) {
                                        continue;
                                    }
                                    // Move item to the closer cluster.
                                    var parentCluster = itemToCluster[compareFeature.id];
                                    var featureIndex = parentCluster.features.indexOf(compareFeature);
                                    if (featureIndex >= 0) {
                                        parentCluster.features.splice(featureIndex, 1);
                                    } else if (parentCluster.centerFeature === compareFeature) {
                                        //Find a new center or delete the cluster
                                        if (parentCluster.features.length > 0) {
                                            //Set a new center to the cluster
                                            parentCluster.centerFeature = parentCluster.features.shift();
                                        } else {
                                            //It's the only feature in the cluster, delete it
                                            if (clusters.indexOf(parentCluster) >= 0) {
                                                clusters.splice(clusters.indexOf(parentCluster), 1);
                                            } //else {
                                                //console.log("WARNING: cluster not found.");
                                            //}
                                        }
                                    } //else {
                                        //console.log("WARNING: Should not be here!");
                                    //}
                                }
                                distanceToCluster[compareFeature.id] = distance;
                                cluster.features.push(compareFeature);
                                itemToCluster[compareFeature.id] = cluster;

                                visitedFeatures.push(compareFeature);
                            }
                        }
                    }
                }

                //Debuging
                var featureCount = 0,
                    s,
                    min = Number.POSITIVE_INFINITY,
                    max = Number.NEGATIVE_INFINITY;

                for (s = clusters.length - 1; s >= 0; s--) {
                    min = clusters[s].features.length + 1 < min ? clusters[s].features.length + 1 : min;
                    max = clusters[s].features.length + 1 > max ? clusters[s].features.length + 1 : max;

                    clusters[s].featureCount = clusters[s].features.length + 1;
                    featureCount += clusters[s].featureCount;
                }

                //console.log("classified feature count: " + featureCount);
                //console.log("Time to calculate clusters: " + (new Date() - time) + " ms");
            }

            this._clusters = clusters;
        }
        return this._clusters;
    };
}
